import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModuleRounded';
import TableRowsIcon from '@mui/icons-material/TableRowsRounded';
import RestartAltIcon from '@mui/icons-material/RestartAltRounded';
import FeedIcon from '@mui/icons-material/Feed';
import InsightsIcon from '@mui/icons-material/InsightsRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import AssignmentIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import PendingActionsIcon from '@mui/icons-material/PendingActionsRounded';
import { saveAs } from 'file-saver';
import moment from 'moment';
import 'moment/locale/fr';
import toast from 'react-hot-toast';

import { questionnaireApi } from '../../api/questionnaireApi';
import QuestionnaireAnswersImport from '../../components/dashboard/questionnaire/QuestionnaireAnswersImport';
import { actionPlanApi } from '../../api/actionPlanApi';
import { reportsApi } from '../../api/reportsApi';
import Scrollbar from '../../components/Scrollbar';
import ScrollJumpButtons, { findScrollParent } from '../../components/ScrollJumpButtons';
import FormationResponseCard from '../../components/dashboard/questionnaire/responses/FormationResponseCard';
import SessionResponseCard from '../../components/dashboard/questionnaire/responses/SessionResponseCard';
import SessionResponsesDrawer from '../../components/dashboard/questionnaire/responses/SessionResponsesDrawer';
import StarRating from '../../components/dashboard/questionnaire/responses/StarRating';
import ResponsesTabs from '../../components/dashboard/questionnaire/responses/ResponsesTabs';
import {
  buildFormationCards,
  formatDateRange,
  formatStyle,
  getTypeConfig,
  groupKey,
  scoreColor,
  sessionStatusStyle,
  sortEntries
} from '../../components/dashboard/questionnaire/responses/constants';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import ChartSquareBarIcon from '../../icons/ChartSquareBar';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey, lightVeil, greyAlpha } from '../../theme/tones';

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Session la plus récente' },
  { value: 'date_asc', label: 'Session la plus ancienne' },
  { value: 'score_desc', label: 'Meilleur score' },
  { value: 'score_asc', label: 'Score le plus faible' },
  { value: 'responses_desc', label: 'Le plus de réponses' },
  { value: 'formation_asc', label: 'Formation (A → Z)' }
];

const EMPTY_FILTERS = {
  formation_id: 'All',
  session_id: 'All',
  year: 'All',
  format: 'All',
  profession: 'All',
  search: ''
};

/* ------------------------------------------------------------------ */
/* KPI                                                                 */
/* ------------------------------------------------------------------ */

const KpiCard = ({ label, value, suffix, hint, color, progress, badge }) => (
  <Card
    sx={{
      p: 2.25,
      borderRadius: '18px',
      border: `1px solid ${grey(100)}`,
      boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }}
  >
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em', color: 'text.disabled', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography sx={{ fontSize: '1.9rem', fontWeight: 900, color: color || grey(900), lineHeight: 1.1 }}>
          {value}
        </Typography>
        {suffix && (
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: color || grey(900) }}>{suffix}</Typography>
        )}
      </Box>
      {badge}
    </Box>
    {progress !== undefined && progress !== null && (
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, progress))}
        sx={{
          height: 5,
          borderRadius: 3,
          mt: 0.5,
          bgcolor: (theme) => alpha(theme.palette.text.disabled, 0.16),
          '& .MuiLinearProgress-bar': { bgcolor: color || grey(900), borderRadius: 3 }
        }}
      />
    )}
    {hint && (
      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600, mt: progress !== undefined ? 0.25 : 0 }}>
        {hint}
      </Typography>
    )}
  </Card>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const QuestionnaireSatisfaction = () => {
  // Le type et la formation dépliée vivent dans l'URL : c'est ce qui permet de
  // revenir sur les sessions de la formation qu'on venait de quitter (retour du
  // plan d'action, rafraîchissement, lien partagé) plutôt que sur la liste des
  // formations.
  const [searchParams, setSearchParams] = useSearchParams();
  const [type, setType] = useState(() => searchParams.get('type') || 'Satisfaction');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [view, setView] = useState('cards');

  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ years: [], formats: [], professions: [], formations: [] });
  // Avancement des plans d'action, toutes formations confondues : sert au
  // bouton de suivi, qui annonce le rapport clôturés / total.
  const [planStats, setPlanStats] = useState(null);
  // Progression pédagogique Pré -> Post. Elle arrive avec la vue d'ensemble,
  // donc filtrée comme le reste du bandeau : changer de formation, de session
  // ou d'année la recalcule sur cette seule sélection.
  const progression = stats?.progression || null;
  // Gain *relatif*, en pourcentage — (post - pré) / pré — calculé par le
  // serveur. Même règle que l'écran Progression, pour que les deux
  // affichent le même nombre.
  const progressionGain = progression && progression.gain !== null && progression.gain !== undefined
    ? Number(progression.gain)
    : null;
  const [loading, setLoading] = useState(true);

  const [formations, setFormations] = useState([]);
  // Les sessions du filtre restent celles de la formation choisie, même quand
  // une session est sélectionnée (sinon la liste se réduirait à un élément).
  const [sessionOptions, setSessionOptions] = useState([]);

  // Vue cartes : niveau 1 = formations, niveau 2 = sessions de la formation
  // ouverte. `null` = on est resté au niveau formations.
  const [openFormationId, setOpenFormationId] = useState(() => searchParams.get('formation') || null);

  // Session mise en avant quand on arrive depuis sa fiche (onglet « Suivi des
  // réponses »). Purement visuel : elle passe en tête et reste cerclée jusqu'à
  // ce qu'on remonte au niveau formations.
  const [highlightSessionId, setHighlightSessionId] = useState(() => searchParams.get('session') || null);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const typeConfig = getTypeConfig(type);
  const accent = typeConfig.accent;

  // Les listes déroulantes ne dépendent que du type : on ne les redemande
  // qu'au changement d'onglet.
  const loadedOptionsFor = useRef(null);
  const requestId = useRef(0);

  /* --- chargement ------------------------------------------------- */

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => clearTimeout(timer);
  }, [filters.search]);


  const queryFilters = useMemo(() => ({
    formation_id: filters.formation_id,
    session_id: filters.session_id,
    year: filters.year,
    format: filters.format,
    profession: filters.profession,
    search: debouncedSearch
  }), [filters.formation_id, filters.session_id, filters.year, filters.format, filters.profession, debouncedSearch]);

  useEffect(() => {
    const controller = new AbortController();
    const current = ++requestId.current;
    const needsOptions = loadedOptionsFor.current !== type;

    (async () => {
      try {
        setLoading(true);
        const accessToken = window.localStorage.getItem('accessToken');
        const res = await questionnaireApi.fetchSubmissionsOverview(
          type,
          queryFilters,
          accessToken,
          needsOptions,
          controller.signal
        );
        if (current !== requestId.current) return;

        if (res && res.status) {
          setGroups(res.groups || []);
          setStats(res.stats || null);
          if (res.options) {
            setOptions(res.options);
            // Le filtre Formation ne propose que les formations ayant répondu :
            // la liste suit donc l'onglet, comme les années et les professions.
            setFormations(res.options.formations || []);
            loadedOptionsFor.current = type;
          }
        } else {
          setGroups([]);
          setStats(null);
          if (res?.message) toast.error(res.message);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [type, queryFilters, refreshToken]);

  // Mémorise les sessions disponibles pour la formation sélectionnée.
  useEffect(() => {
    if (filters.formation_id === 'All') {
      setSessionOptions([]);
      return;
    }
    if (filters.session_id !== 'All') return;
    setSessionOptions(
      groups
        .filter((g) => String(g.formation_id) === String(filters.formation_id))
        .map((g) => ({ id: g.session_id, number: g.session_number, start_date: g.start_date }))
    );
  }, [groups, filters.formation_id, filters.session_id]);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const res = await actionPlanApi.getOpenPlans(accessToken, controller.signal);
        if (res && res.status) setPlanStats(res.stats || null);
      } catch (err) {
        // Le suivi des plans d'action est un à-côté : son indisponibilité ne
        // doit pas parasiter la lecture des réponses.
        if (err.name !== 'AbortError') console.error(err);
      }
    })();
    return () => controller.abort();
  }, []);

  /* --- interactions ------------------------------------------------ */

  const patchFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setDebouncedSearch('');
    setSortBy('date_desc');
    setOpenFormationId(null);
    setHighlightSessionId(null);
  };

  const activeFilterCount = useMemo(() => (
    Object.entries(filters).filter(([key, value]) => value !== EMPTY_FILTERS[key]).length
  ), [filters]);

  const sortedGroups = useMemo(() => sortEntries(groups, sortBy), [groups, sortBy]);

  const formationCards = useMemo(
    () => sortEntries(buildFormationCards(groups), sortBy),
    [groups, sortBy]
  );

  const openFormation = useMemo(
    () => (openFormationId === null
      ? null
      : formationCards.find((f) => String(f.formation_id) === String(openFormationId)) || null),
    [formationCards, openFormationId]
  );

  // Les sessions affichées au niveau 2 : celles de la formation dépliée. La
  // session mise en avant passe en tête — la cercler sans la remonter ne
  // servirait à rien sur une formation qui en compte vingt.
  const openFormationSessions = useMemo(() => {
    if (openFormationId === null) return [];
    const list = sortedGroups.filter((g) => String(g.formation_id) === String(openFormationId));
    if (!highlightSessionId) return list;
    const targeted = (g) => String(g.session_id) === String(highlightSessionId);
    return [...list.filter(targeted), ...list.filter((g) => !targeted(g))];
  }, [sortedGroups, openFormationId, highlightSessionId]);

  // Remonter d'un niveau, changer de formation ou réinitialiser fait tomber la
  // mise en avant : elle ne vaut que pour la session d'où l'on vient.
  const closeFormation = useCallback(() => {
    setOpenFormationId(null);
    setHighlightSessionId(null);
  }, []);

  // Contexte emporté vers les deux vues d'analyse : la formation dépliée (ou
  // choisie dans le filtre, ce qui revient au même) et, le cas échéant, la
  // session filtrée. Sans contexte on ne passe rien : les deux pages gardent
  // alors leur sélection manuelle habituelle.
  const contextFormationId = openFormationId !== null && openFormationId !== ''
    ? openFormationId
    : (filters.formation_id !== 'All' ? filters.formation_id : null);
  // La session vient soit du filtre, soit de la fiche session d'où l'on arrive.
  const contextSessionId = contextFormationId === null
    ? null
    : (filters.session_id !== 'All' ? filters.session_id : highlightSessionId);

  // `type` n'est utile qu'au retour : il ramène sur le bon onglet.
  const contextQuery = useMemo(() => {
    if (contextFormationId === null) return '';
    const params = new URLSearchParams({ type, formation: String(contextFormationId) });
    if (contextSessionId !== null) params.set('session', String(contextSessionId));
    return `?${params.toString()}`;
  }, [type, contextFormationId, contextSessionId]);

  // Sert uniquement à annoncer la destination dans l'infobulle des boutons.
  const contextLabel = useMemo(() => {
    if (contextFormationId === null) return '';
    const name = openFormation?.formation_name
      || formations.find((f) => String(f.id) === String(contextFormationId))?.name
      || 'la formation sélectionnée';
    const session = contextSessionId === null
      ? null
      : sessionOptions.find((s) => String(s.id) === String(contextSessionId));
    return session ? `${name} · session N° ${session.number}` : name;
  }, [contextFormationId, contextSessionId, openFormation, formations, sessionOptions]);

  // Reflète le niveau courant dans l'URL, en `replace` pour ne pas empiler une
  // entrée d'historique à chaque clic. La comparaison à la recherche réelle
  // évite la boucle : setSearchParams change d'identité à chaque rendu.
  useEffect(() => {
    const next = new URLSearchParams();
    next.set('type', type);
    if (openFormationId !== null && openFormationId !== '') {
      next.set('formation', String(openFormationId));
    }
    if (highlightSessionId) {
      next.set('session', String(highlightSessionId));
    }
    if (next.toString() !== window.location.search.replace(/^\?/, '')) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, openFormationId, highlightSessionId]);

  // Un changement de type ou de filtres peut faire disparaître la formation
  // dépliée : on remonte alors d'un niveau plutôt que d'afficher du vide.
  useEffect(() => {
    if (openFormationId === null || loading) return;
    if (!groups.some((g) => String(g.formation_id) === String(openFormationId))) {
      closeFormation();
    }
  }, [groups, openFormationId, loading, closeFormation]);

  // Idem pour la session mise en avant : changer d'onglet peut la faire sortir
  // de la liste (pas de réponse de ce type), auquel cas la mise en avant n'a
  // plus d'objet.
  useEffect(() => {
    if (!highlightSessionId || loading) return;
    if (!groups.some((g) => String(g.session_id) === String(highlightSessionId))) {
      setHighlightSessionId(null);
    }
  }, [groups, highlightSessionId, loading]);

  const refreshOverview = useCallback(() => setRefreshToken((n) => n + 1), []);

  // Ce n'est pas la fenêtre qui défile mais le conteneur de contenu du
  // DashboardLayout : on le retrouve en remontant depuis une ancre du DOM.
  const anchorRef = useRef(null);
  const getPageScroller = useCallback(() => findScrollParent(anchorRef.current), []);

  // Le compte rendu est désormais un .docx monté côté serveur (PhpWord) : le
  // front ne compose plus le document, il enregistre le binaire reçu.
  const handleDownloadReport = async (group) => {
    const toastId = 'satisfaction-report';
    try {
      setReportLoading(true);
      toast.loading('Préparation du compte rendu…', { id: toastId });

      const accessToken = window.localStorage.getItem('accessToken');
      const blob = await reportsApi.downloadSessionWordReport(group.session_id, accessToken);

      saveAs(blob, `Compte_rendu_session_${group.session_number ?? group.session_id}.docx`);
      toast.success('Compte rendu téléchargé', { id: toastId });
    } catch (error) {
      toast.error(error.message || 'Compte rendu indisponible', { id: toastId });
    } finally {
      setReportLoading(false);
    }
  };

  /* --- rendu -------------------------------------------------------- */

  const inputSx = {
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-root': { borderRadius: '12px' }
  };

  // Le tableau reste à plat : seul le mode cartes a deux niveaux.
  const atFormationLevel = view === 'cards' && openFormationId === null;
  const countLabel = atFormationLevel
    ? `${formationCards.length} formation${formationCards.length > 1 ? 's' : ''}`
    : `${sortedGroups.length} session${sortedGroups.length > 1 ? 's' : ''}`;

  return (
    <>
      <Helmet>
        <title>Résultats des questionnaires | Qualité | Apteed</title>
      </Helmet>

      <Box ref={anchorRef} sx={{ bgcolor: 'action.hover', minHeight: '100%', py: 3 }}>
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>

          {/* ── En-tête ───────────────────────────────────────────── */}
          <Box
            sx={{
              mb: 2.5,
              p: 3,
              borderRadius: '24px',
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              background: `linear-gradient(135deg, ${lightVeil(0.96)} 0%, ${greyAlpha(100, 0.75)} 100%)`,
              boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box
                sx={{
                  width: 54, height: 54, borderRadius: '16px', flexShrink: 0,
                  background: typeConfig.gradient,
                  display: 'grid', placeItems: 'center',
                  boxShadow: `0 8px 20px ${accent}4D`,
                  transition: 'background .3s ease, box-shadow .3s ease'
                }}
              >
                <FeedIcon sx={{ color: grey(0), fontSize: '1.6rem' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.45rem', color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  Résultats des questionnaires
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.87rem', mt: 0.5 }}>
                  {typeConfig.hint}
                  {stats?.last_response_at && ` · Dernière réponse ${moment(stats.last_response_at).fromNow()}`}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/dashboard"
                startIcon={<ArrowLeftIcon style={{ fontSize: 13 }} />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
              >
                Retour
              </Button>
              {/* Le suivi des plans d'action se lit à part : la question n'est
                  plus « qu'ont répondu les apprenants ? » mais « qu'avons-nous
                  fait de leurs remarques, et qu'est-ce qui traîne ? ». */}
              {planStats && planStats.total > 0 && (
                <Tooltip title={`${planStats.open} session${planStats.open > 1 ? 's' : ''} dont le plan d’action n’est pas clôturé · ${planStats.closed} clôturé${planStats.closed > 1 ? 's' : ''} sur ${planStats.total} session${planStats.total > 1 ? 's' : ''} ayant un plan`}>
                  <Button
                    component={RouterLink}
                    to="/dashboard/plans-action-ouverts"
                    variant="outlined"
                    startIcon={<PendingActionsIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 800,
                      borderColor: planStats.open > 0 ? getTone('amber').base : 'divider',
                      color: planStats.open > 0 ? getTone('amber').text : 'text.primary',
                      bgcolor: planStats.open > 0 ? toneAlpha('amber', 0.08) : 'transparent'
                    }}
                  >
                    {`Plans d’action ${planStats.closed}/${planStats.total}`}
                  </Button>
                </Tooltip>
              )}

              {/* L'import des réponses vit ici plutôt qu'avec les questionnaires :
                  ce sont des résultats, et c'est cet écran qui les montre.
                  `requestId` force la vue d'ensemble à se recharger après coup. */}
              <QuestionnaireAnswersImport onImported={() => setRefreshToken((v) => v + 1)} />
              {/* Les deux vues d'analyse reprennent la formation (et la session)
                  en cours : inutile de la ressaisir de l'autre côté. */}
              <Tooltip title={contextLabel ? `Ouvre directement sur ${contextLabel}` : ''}>
                <Button
                  component={RouterLink}
                  to={`/dashboard/questionnaire-progression${contextQuery}`}
                  variant="outlined"
                  startIcon={<ChartSquareBarIcon fontSize="small" />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, borderColor: 'divider', color: 'text.primary' }}
                >
                  Visual. Pré/Post
                </Button>
              </Tooltip>
              <Tooltip title={contextLabel ? `Ouvre directement sur ${contextLabel}` : ''}>
                <Button
                  component={RouterLink}
                  to={`/dashboard/visualisation-globale${contextQuery}`}
                  variant="contained"
                  startIcon={<InsightsIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    borderRadius: '10px', textTransform: 'none', fontWeight: 800,
                    background: typeConfig.gradient, boxShadow: `0 4px 12px ${accent}4D`
                  }}
                >
                  Vue globale
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* ── Sélecteur de type ─────────────────────────────────── */}
          <ResponsesTabs
            active={type}
            onSelectType={setType}
            context={{ type, formationId: contextFormationId, sessionId: contextSessionId }}
          />

          {/* ── KPI ───────────────────────────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6} md={2.4}>
              <KpiCard
                label={typeConfig.scoreLabel + ' moyenne'}
                value={stats?.average !== null && stats?.average !== undefined ? stats.average.toFixed(1) : '—'}
                suffix={stats?.average !== null && stats?.average !== undefined ? '%' : ''}
                color={scoreColor(stats?.average)}
                progress={stats?.average ?? 0}
                badge={typeConfig.rating ? <StarRating percent={stats?.average} size="0.95rem" iconSize="1.15rem" /> : null}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              {/* Gain pédagogique entre le pré-formation et le post-formation,
                  sur la sélection courante. Le tiret couvre le cas fréquent
                  d'une sélection qui a des satisfactions mais pas les deux
                  phases — mieux vaut ne rien annoncer qu'un faux zéro. */}
              <KpiCard
                label="Progression moyenne"
                value={progressionGain === null ? '—' : (progressionGain > 0 ? `+${progressionGain.toFixed(1)}` : progressionGain.toFixed(1))}
                suffix={progressionGain === null ? '' : ' %'}
                color={scoreColor(progressionGain === null ? null : Math.min(100, Math.max(0, progressionGain)))}
                hint={
                  progressionGain === null
                    ? 'Pré → Post non disponible ici'
                    : `Pré ${Number(progression.pre ?? 0).toFixed(1)} % → Post ${Number(progression.post ?? 0).toFixed(1)} %`
                }
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <KpiCard label="Réponses" value={stats?.responses ?? 0} color={accent} hint="Questionnaires remplis" />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <KpiCard label="Apprenants" value={stats?.learners ?? 0} color={grey(900)} hint="Répondants distincts" />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <KpiCard
                label="Sessions couvertes"
                value={stats?.sessions ?? 0}
                color={grey(900)}
                hint={`sur ${stats?.formations ?? 0} formation${(stats?.formations ?? 0) > 1 ? 's' : ''}`}
              />
            </Grid>
          </Grid>

          {/* ── Filtres ───────────────────────────────────────────── */}
          <Card sx={{ p: 2, mb: 2.5, borderRadius: '18px', border: `1px solid ${grey(100)}`, boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}` }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Formation, apprenant, e-mail…"
                value={filters.search}
                onChange={(e) => patchFilters({ search: e.target.value })}
                sx={{ ...inputSx, flex: '1 1 260px', minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }}
              />

              <Autocomplete
                size="small"
                options={formations}
                value={formations.find((f) => String(f.id) === String(filters.formation_id)) || null}
                getOptionLabel={(o) => (o ? `${o.name}${o.programNumberAll ? ` (N° ${o.programNumberAll})` : ''}`.trim() : '')}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(e, value) => {
                  // Choisir une formation dans le filtre revient à cliquer sur
                  // sa carte : on déplie directement ses sessions.
                  patchFilters({ formation_id: value ? value.id : 'All', session_id: 'All' });
                  setOpenFormationId(value ? value.id : null);
                  setHighlightSessionId(null);
                }}
                sx={{ flex: '2 1 320px', minWidth: 260 }}
                renderInput={(params) => (
                  <TextField {...params} label="Formation" variant="outlined" sx={inputSx} />
                )}
              />

              <TextField
                select size="small" label="Année" value={filters.year}
                onChange={(e) => patchFilters({ year: e.target.value })}
                sx={{ ...inputSx, flex: '0 1 120px', minWidth: 110 }}
              >
                <MenuItem value="All">Toutes</MenuItem>
                {options.years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Format" value={filters.format}
                onChange={(e) => patchFilters({ format: e.target.value })}
                sx={{ ...inputSx, flex: '0 1 170px', minWidth: 150 }}
              >
                <MenuItem value="All">Tous</MenuItem>
                {options.formats.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Profession" value={filters.profession}
                onChange={(e) => patchFilters({ profession: e.target.value })}
                sx={{ ...inputSx, flex: '0 1 190px', minWidth: 160 }}
              >
                <MenuItem value="All">Toutes</MenuItem>
                {options.professions.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>

              {filters.formation_id !== 'All' && (
                <TextField
                  select size="small" label="Session" value={filters.session_id}
                  onChange={(e) => patchFilters({ session_id: e.target.value })}
                  sx={{ ...inputSx, flex: '0 1 140px', minWidth: 130 }}
                >
                  <MenuItem value="All">Toutes</MenuItem>
                  {sessionOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>N° {s.number}</MenuItem>
                  ))}
                </TextField>
              )}
            </Box>

            <Divider sx={{ my: 1.75, borderColor: 'divider' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'text.primary' }}>
                {loading ? 'Chargement…' : countLabel}
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} filtre${activeFilterCount > 1 ? 's' : ''} actif${activeFilterCount > 1 ? 's' : ''}`}
                  size="small"
                  onDelete={resetFilters}
                  deleteIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
                  sx={{ bgcolor: `${accent}1A`, color: accent, fontWeight: 800, fontSize: '0.68rem', height: 22 }}
                />
              )}

              <Box sx={{ flexGrow: 1 }} />

              <TextField
                select size="small" label="Trier par" value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ ...inputSx, minWidth: 210 }}
              >
                {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(e, v) => v && setView(v)}
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiToggleButton-root': { border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: '10px !important', px: 1.25 },
                  '& .Mui-selected': { bgcolor: `${accent}1A !important`, color: `${accent} !important` }
                }}
              >
                <ToggleButton value="cards"><Tooltip title="Cartes"><ViewModuleIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
                <ToggleButton value="table"><Tooltip title="Tableau"><TableRowsIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Card>

          {/* ── Résultats ─────────────────────────────────────────── */}
          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 12 }}>
              <CircularProgress sx={{ color: accent }} />
            </Box>
          )}

          {!loading && sortedGroups.length === 0 && (
            <Card sx={{ py: 10, textAlign: 'center', borderRadius: '20px', border: `1px solid ${grey(100)}`, boxShadow: 'none' }}>
              <FeedIcon sx={{ fontSize: 44, color: grey(300), mb: 1.5 }} />
              <Typography sx={{ fontWeight: 800, color: 'text.secondary', mb: 0.5 }}>
                Aucune réponse « {type} »
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled', mb: 2.5 }}>
                {activeFilterCount > 0
                  ? 'Aucune session ne correspond aux filtres sélectionnés.'
                  : 'Aucun questionnaire de ce type n’a encore été rempli.'}
              </Typography>
              {activeFilterCount > 0 && (
                <Button
                  onClick={resetFilters}
                  startIcon={<RestartAltIcon sx={{ fontSize: 17 }} />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: accent }}
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </Card>
          )}

          {!loading && sortedGroups.length > 0 && view === 'cards' && atFormationLevel && (
            <Grid container spacing={2}>
              {formationCards.map((formation) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={formation.formation_id}>
                  <FormationResponseCard
                    formation={formation}
                    accent={accent}
                    showRating={Boolean(typeConfig.rating)}
                    onOpen={(f) => setOpenFormationId(f.formation_id)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {!loading && sortedGroups.length > 0 && view === 'cards' && !atFormationLevel && (
            <>
              <Card
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '18px',
                  border: `1px solid ${grey(100)}`,
                  boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap'
                }}
              >
                <Button
                  onClick={closeFormation}
                  startIcon={<ArrowLeftIcon style={{ fontSize: 13 }} />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
                >
                  Toutes les formations
                </Button>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {openFormation?.formation_name || 'Formation inconnue'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 600 }}>
                    {openFormationSessions.length} session{openFormationSessions.length > 1 ? 's' : ''}
                    {openFormation?.program_number ? ` · N° ${openFormation.program_number}` : ''}
                  </Typography>
                </Box>
              </Card>

              <Grid container spacing={2}>
                {openFormationSessions.map((group) => {
                  const targeted = highlightSessionId !== null
                    && String(group.session_id) === String(highlightSessionId);
                  return (
                    <Grid item xs={12} sm={6} lg={4} xl={3} key={groupKey(group)}>
                      <Box
                        sx={targeted
                          ? { borderRadius: '22px', outline: `2px solid ${accent}`, outlineOffset: '2px', height: '100%' }
                          : { height: '100%' }}
                      >
                        <SessionResponseCard
                          group={group}
                          accent={accent}
                          showRating={Boolean(typeConfig.rating)}
                          onOpen={setSelectedGroup}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Le plan d'action est porté par la formation : le même contenu
                  s'ouvre depuis Pré-Formation, Post-Formation ou Satisfaction.
                  Le type n'est transmis que pour recolorer la page et
                  pré-remplir le thème du formulaire. */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3.5 }}>
                <Button
                  component={RouterLink}
                  to={`/dashboard/plan-action/${openFormationId}?type=${encodeURIComponent(type)}`}
                  variant="contained"
                  startIcon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 800,
                    px: 3.5,
                    py: 1.15,
                    background: typeConfig.gradient,
                    boxShadow: `0 6px 18px ${accent}4D`
                  }}
                >
                  Plan d’action
                </Button>
              </Box>
            </>
          )}

          {!loading && sortedGroups.length > 0 && view === 'table' && (
            <Card sx={{ borderRadius: '18px', border: `1px solid ${grey(100)}`, boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`, overflow: 'hidden' }}>
              <Scrollbar>
                <Box sx={{ minWidth: 1140 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Formation', 'Session', 'Dates', 'Format', 'Formateur', 'Professions', 'Réponses', typeConfig.scoreLabel, ''].map((h, i) => (
                          <TableCell
                            key={h || i}
                            align={['Réponses', typeConfig.scoreLabel].includes(h) ? 'center' : 'left'}
                            sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', bgcolor: 'action.hover', whiteSpace: 'nowrap' }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedGroups.map((g) => {
                        const color = scoreColor(g.average);
                        const fmt = formatStyle(g.format);
                        const status = sessionStatusStyle(g.session_status);
                        const trainer = (g.trainer || '').trim();
                        return (
                          <TableRow
                            key={groupKey(g)}
                            hover
                            onClick={() => setSelectedGroup(g)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ maxWidth: 380 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.35 }}>
                                {g.formation_name || 'Formation inconnue'}
                              </Typography>
                              {g.program_number && (
                                <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 700 }}>
                                  N° {g.program_number}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: 'text.primary' }}>
                                  N° {g.session_number ?? '—'}
                                </Typography>
                                {g.session_status && (
                                  <Chip
                                    label={g.session_status}
                                    size="small"
                                    sx={{ bgcolor: status.bg, color: status.color, fontWeight: 800, fontSize: '0.6rem', height: 18 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {formatDateRange(g.start_date, g.end_date)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={g.format || '—'}
                                size="small"
                                sx={{ bgcolor: fmt.bg, color: fmt.color, fontWeight: 800, fontSize: '0.63rem', height: 20 }}
                              />
                            </TableCell>
                            {/* Même lecture que sur la carte : le champ « Formateur »
                                de la fiche session, souvent vide sur l'historique. */}
                            <TableCell sx={{ maxWidth: 200 }}>
                              <Typography
                                noWrap
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: trainer ? 'text.secondary' : 'text.disabled',
                                  fontStyle: trainer ? 'normal' : 'italic'
                                }}
                              >
                                {trainer || 'Pas encore inséré'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                                {g.professions.length ? g.professions.join(', ') : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'text.primary' }}>
                                {g.responses}
                              </Typography>
                              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 700 }}>
                                {g.learners} appr.
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 54, height: 6, borderRadius: 3, bgcolor: (theme) => alpha(theme.palette.text.disabled, 0.18), overflow: 'hidden' }}>
                                    <Box sx={{ width: `${Math.max(0, Math.min(100, g.average ?? 0))}%`, height: '100%', bgcolor: color }} />
                                  </Box>
                                  <Typography sx={{ fontWeight: 900, fontSize: '0.78rem', color, minWidth: 42 }}>
                                    {g.average === null || g.average === undefined ? '—' : `${g.average}%`}
                                  </Typography>
                                </Box>
                                {typeConfig.rating && (
                                  <StarRating percent={g.average} size="0.72rem" color={color} iconSize="0.9rem" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" sx={{ color: accent }}>
                                <ArrowForwardIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Scrollbar>
            </Card>
          )}
        </Container>

        <ScrollJumpButtons
          getTarget={getPageScroller}
          watch={`${view}-${openFormationId ?? 'all'}-${sortedGroups.length}-${loading}`}
          accent={accent}
          offset={24}
          fixed
        />
      </Box>

      <SessionResponsesDrawer
        group={selectedGroup}
        type={type}
        accent={accent}
        onClose={() => setSelectedGroup(null)}
        onDeleted={refreshOverview}
        onDownloadReport={handleDownloadReport}
        reportLoading={reportLoading}
      />
    </>
  );
};

export default QuestionnaireSatisfaction;
